<template>
	<div class="ci-upload-ledger">
		<header class="ul-head">
			<p class="ul-eyebrow">Importaci&oacute;n</p>
			<h2 class="ul-heading">Subir mayor</h2>
			<p class="ul-sub">Importa el libro mayor y define su corte.</p>
		</header>
		<!-- Grid de dos columnas: el flujo de subida (body + footer) a la
			izquierda; el contexto de la empresa en el aside de la derecha. -->
		<section class="ledger-body">
			<!-- Fecha de corte (mayor <-> SII) -->
			<div class="field-group">
				<div class="field-eyebrow">
					Fecha de corte
					<span class="field-eyebrow-note">mayor &harr; SII</span>
				</div>
				<p class="field-hint">
					Hasta esta fecha el reporte usa el <strong>mayor</strong>; despu&eacute;s,
					el <strong>SII</strong>. Vac&iacute;a = solo SII &middot; <strong>Solo mayor</strong>
					= la empresa no usa SII.
				</p>
				<b-field>
					<b-datepicker
						v-model="cutoffDate"
						placeholder="Selecciona la fecha de corte"
						icon="calendar-today"
						locale="es-CL"
						:editable="true"
						:loading="loadingCutoff"
						trap-focus
						expanded
					/>
				</b-field>
				<p v-if="isLedgerOnly" class="ledger-only-hint">
					Solo mayor: el reporte usa el mayor en todas las fechas.
				</p>
				<div class="cutoff-actions">
					<b-button
						class="is-light"
						:disabled="isLedgerOnly || savingCutoff"
						@click="setLedgerOnly()"
					>
						Solo mayor
					</b-button>
					<b-button
						class="is-light"
						:disabled="!cutoffDate || savingCutoff"
						@click="clearCutoff()"
					>
						Limpiar
					</b-button>
					<b-button
						class="is-primary"
						:disabled="savingCutoff"
						:loading="savingCutoff"
						@click="saveCutoff()"
					>
						Guardar corte
					</b-button>
				</div>
			</div>

			<!-- Tipo de negocio: uno solo. Una columna por super_type
				(Servicios | Productos) para diferenciarlos de un vistazo. -->
			<div class="field-group">
				<div class="field-eyebrow">
					Tipo de negocio
					<span class="field-eyebrow-note">obligatorio</span>
				</div>
				<div class="business-types">
					<div
						v-for="group in businessTypesBySuper"
						:key="group.superType"
						class="business-type-col"
					>
						<div class="business-type-col-head">{{ group.superType }}</div>
						<b-radio
							v-for="bt in group.types"
							:key="bt.name"
							v-model="businessType"
							:native-value="bt.name"
							name="ledgerBusinessType"
							type="is-primary"
							class="business-type-radio"
						>
							{{ bt.name }}
						</b-radio>
					</div>
				</div>
			</div>

			<!-- Alcance de la subida (carga inicial vs. actualizar un año) -->
			<div class="field-group">
				<div class="field-eyebrow">Alcance de la subida</div>
				<p v-if="ledgerYearsLoaded.length" class="field-hint">
					A&ntilde;os cargados: <strong>{{ ledgerYearsLoaded.join(" &middot; ") }}</strong>
				</p>
				<!-- Toggle + selector de año en la MISMA fila (el select aparece a la
					derecha del botón, sin ocupar altura extra). -->
				<div class="scope-row">
					<b-field class="scope-mode">
						<b-radio-button v-model="uploadMode" native-value="full" type="is-primary">
							Carga inicial
						</b-radio-button>
						<b-radio-button v-model="uploadMode" native-value="year" type="is-primary">
							Actualizar a&ntilde;o
						</b-radio-button>
					</b-field>
					<b-field v-if="uploadMode === 'year'" class="scope-year">
						<b-select v-model="selectedYear" placeholder="Elige el año">
							<option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
						</b-select>
					</b-field>
				</div>
				<p class="field-hint mt-1">
					<template v-if="uploadMode === 'year'">
						Reemplaza <strong>solo {{ selectedYear || "el a&ntilde;o elegido" }}</strong>;
						los dem&aacute;s a&ntilde;os no se tocan. El archivo debe contener solo ese a&ntilde;o.
					</template>
					<template v-else>
						Reemplaza <strong>todo</strong> el mayor (hist&oacute;rico completo).
					</template>
				</p>
			</div>

			<!-- Planilla del mayor (.xlsx) -->
			<div class="field-group">
				<div class="field-eyebrow">
					Planilla del mayor
					<span class="field-eyebrow-note">.xlsx</span>
				</div>
				<UploadFile
					:key="uploadFileKey"
					v-show="!uploadingLedger"
					fileName="planilla del mayor"
					messageFormat="XLSX"
					extensionAccept=".xlsx"
					:isLoading="uploadingLedger"
					@uploaded="onUploaded"
					@removed-file="onRemovedFile"
				/>

				<!-- Readout del import async: reemplaza el dropzone mientras corre.
				% real del meta del job, o pista indeterminada si aún no reporta. -->
				<div v-if="uploadingLedger" class="import-readout">
					<div class="import-readout-head">
						<div class="import-readout-text">
							<span class="import-readout-title">Importando mayor&hellip;</span>
							<span v-if="fileLedger" class="import-readout-file">
								{{ fileLedger.name }}
							</span>
						</div>
						<span v-if="progress !== null" class="import-readout-pct">
							{{ progress }}<span class="import-readout-pct-unit">%</span>
						</span>
					</div>
					<div
						class="track"
						:class="{ 'is-indeterminate': progress === null }"
					>
						<div
							class="track-fill"
							:style="progress === null ? null : { width: progress + '%' }"
						></div>
					</div>
				</div>
			</div>

			<!-- Filas que bloquean el import (todo-o-nada): no se subió nada. -->
			<div v-if="invalidRows.length" class="import-errors">
				<div class="import-errors-head">
					No se import&oacute; nada &middot; {{ invalidRows.length }}
					{{ invalidRows.length === 1 ? "fila con problema" : "filas con problemas" }}
				</div>
				<p class="import-errors-hint">
					Corrige estas filas en el Excel y vuelve a subirlo.
				</p>
				<ul class="import-errors-list">
					<li
						v-for="(err, i) in invalidRows"
						:key="i"
						class="import-errors-item"
					>
						<span class="import-errors-row">Fila {{ err.row }}</span>
						<span class="import-errors-reason">{{ err.reason }}</span>
						<span v-if="err.document || err.account" class="import-errors-meta">
							{{ err.document
							}}<template v-if="err.account"> &middot; {{ err.account }}</template>
						</span>
					</li>
				</ul>
			</div>

			<!-- Error global del import (sin filas específicas, p.ej. documento
				duplicado o worker caído): queda visible en la sección — el toast de
				notificate desaparece antes de poder leerlo con calma. -->
			<div v-if="importError" class="import-errors">
				<div class="import-errors-head">No se import&oacute; el mayor</div>
				<p class="import-errors-hint">{{ importError }}</p>
			</div>
		</section>
		<footer class="ledger-foot">
			<!-- Por qué está deshabilitado "Subir mayor": el tipo de negocio es
				obligatorio (define el reporte) y aún no hay uno elegido. -->
			<span v-if="fileLedger && !businessType" class="foot-hint">
				Elige un tipo de negocio para subir.
			</span>
			<b-button
				class="is-primary"
				aria-label="Subir mayor"
				:disabled="!canSubmitLedger"
				:loading="uploadingLedger"
				@click="submitLedger()"
			>
				{{ uploadingLedger ? "Importando…" : "Subir mayor" }}
			</b-button>
		</footer>

		<!-- Columna de contexto: qué hay cargado, antes de decidir qué subir. -->
		<aside class="ul-side">
			<!-- El último intento falló: contexto al entrar. Durante el flujo
				activo el error ya se ve en la columna izquierda; acá solo si no. -->
			<div
				v-if="lastUpload && lastUpload.last_ledger_import_status === 'E' && !uploadingLedger && !importError && !invalidRows.length"
				class="ul-last-error"
			>
				<span class="ul-last-error-title">El &uacute;ltimo intento fall&oacute;</span>
				<p v-if="lastUpload.last_ledger_import_error" class="ul-last-error-text">
					{{ lastUpload.last_ledger_import_error }}
				</p>
			</div>

			<!-- Última importación exitosa: las columnas last_ledger_* solo se
				escriben en éxito, así que la franja sobrevive a intentos rotos. -->
			<div
				v-if="lastUpload && lastUpload.last_ledger_upload_at"
				class="last-upload"
			>
				<div class="last-upload-main">
					<span class="last-upload-eyebrow">&Uacute;ltima importaci&oacute;n</span>
					<span class="last-upload-date">
						{{ formatDateTime(lastUpload.last_ledger_upload_at) }}
					</span>
					<span v-if="lastUpload.last_ledger_uploaded_by" class="last-upload-by">
						por {{ lastUpload.last_ledger_uploaded_by }}
					</span>
				</div>
				<div class="last-upload-stats">
					<span class="stat">
						<span class="stat-value">{{ lastUpload.last_ledger_documents_created }}</span>
						<span class="stat-label">docs</span>
					</span>
					<span class="stat">
						<span class="stat-value">{{ lastUpload.last_ledger_rows_parsed }}</span>
						<span class="stat-label">filas</span>
					</span>
				</div>
			</div>

			<!-- Cobertura mensual del mayor: qué meses tienen documentos, para
				decidir el alcance (carga inicial vs. actualizar un año) a la
				vista antes de subir. -->
			<div v-if="monthsLoaded.length" class="field-group">
				<div class="field-eyebrow">Meses cargados</div>
				<MonthCoverageGrid :monthsLoaded="monthsLoaded" />
			</div>

			<!-- Estado vacío: la empresa no tiene mayor cargado. -->
			<div v-if="lastUpload && !hasSideContext" class="ul-empty">
				<span class="ul-empty-title">Sin importaciones todav&iacute;a</span>
				<p class="ul-empty-text">
					Esta empresa no tiene libro mayor cargado. Sube la planilla
					para crear los documentos de sus meses.
				</p>
			</div>
		</aside>
	</div>
</template>

<script>
import MonthCoverageGrid from "@/components/companyInfo/MonthCoverageGrid";
import UploadFile from "@/components/uploadFile/UploadFile";
import {
	getLedgerCutoff,
	setLedgerCutoff,
	uploadLedger,
	getLedgerLastUpload,
} from "@/api/carteraAnalista";
import { consoleHandlerError, notificate } from "@/utils/message";
import moment from "moment";

// Corte centinela "todo mayor": una fecha lejana (regla emitted_at <= corte →
// mayor) hace que la empresa muestre el mayor en todas las fechas sin tener que
// re-actualizar el corte. Empresas que solo usan mayor (sin SII/Fintoc).
const LEDGER_ONLY_CUTOFF = "2099-12-31";

// La importación del mayor corre async (RQ): el POST responde 202 y el estado
// se sondea vía last_upload (last_ledger_import_status: P/S/E). Sondeo cada 3 s
// con tope de ~5 min para no dejar la sección colgada si el worker se atasca.
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 100;

export default {
	name: "CompanyInfoUploadLedger",
	components: {
		MonthCoverageGrid,
		UploadFile,
	},
	props: {
		idCompany: {
			type: [Number, String],
			required: true,
			default: null,
		},
	},
	data() {
		return {
			cutoffDate: null,
			loadingCutoff: false,
			savingCutoff: false,
			fileLedger: null,
			uploadingLedger: false,
			lastUpload: null,
			// `businessType` = nombre del tipo elegido (uno de los 5);
			// `businessTypes` = opciones que devuelve el backend para los radios.
			businessType: null,
			businessTypes: [],
			pollTimer: null,
			pollAttempts: 0,
			progress: null,
			invalidRows: [],
			// Error global del último import (sin filas): persiste en la sección
			// para leerlo con calma (el toast desaparece solo).
			importError: "",
			// Alcance: "full" (carga inicial, reemplaza todo) o "year" (reemplaza
			// solo el año elegido). `selectedYear` arranca en el año en curso.
			uploadMode: "full",
			selectedYear: moment().year(),
			// Bumpeando esta key remontamos UploadFile para resetear su chip
			// interno (tras cada intento hay que subir otro archivo).
			uploadFileKey: 0,
		};
	},
	created() {
		this.loadCutoff();
		this.loadLastUpload();
	},
	beforeDestroy() {
		this.clearPollTimer();
	},
	computed: {
		isLedgerOnly() {
			return (
				!!this.cutoffDate &&
				moment(this.cutoffDate).format("YYYY-MM-DD") === LEDGER_ONLY_CUTOFF
			);
		},
		// Años ya cargados (derivados en el backend desde emitted_at).
		ledgerYearsLoaded() {
			return (this.lastUpload && this.lastUpload.ledger_years_loaded) || [];
		},
		// Cobertura mensual para el grid compartido (derivada en el backend).
		monthsLoaded() {
			return (this.lastUpload && this.lastUpload.months_loaded) || [];
		},
		// La columna de contexto tiene algo que mostrar (franja de última
		// importación o cobertura); si no, va el estado vacío.
		hasSideContext() {
			return (
				!!(this.lastUpload && this.lastUpload.last_ledger_upload_at) ||
				this.monthsLoaded.length > 0
			);
		},
		// Opciones del selector: años ya cargados + el año en curso y los 5
		// previos, de más nuevo a más viejo (sin duplicados).
		yearOptions() {
			const current = moment().year();
			const years = new Set(this.ledgerYearsLoaded);
			for (let y = current; y >= current - 5; y--) years.add(y);
			return Array.from(years).sort((a, b) => b - a);
		},
		// "Subir mayor" habilitado sólo con archivo + tipo de negocio elegido (el
		// tipo es obligatorio: define qué muestra el reporte). Si la empresa ya
		// tiene uno, viene precargado y no hace falta re-elegirlo.
		canSubmitLedger() {
			return !!this.fileLedger && !this.uploadingLedger && !!this.businessType;
		},
		// Tipos de negocio agrupados por super_type, una columna cada uno:
		// Servicios primero (3), Productos después (2).
		businessTypesBySuper() {
			return ["Servicios", "Productos"]
				.map((superType) => ({
					superType,
					types: this.businessTypes.filter((bt) => bt.super_type === superType),
				}))
				.filter((group) => group.types.length);
		},
	},
	methods: {
		loadCutoff() {
			this.loadingCutoff = true;
			getLedgerCutoff(this.idCompany)
				.then(({ data }) => {
					this.cutoffDate = data.ledger_cutoff_date
						? moment(data.ledger_cutoff_date, "YYYY-MM-DD").toDate()
						: null;
				})
				.catch((error) => {
					consoleHandlerError({
						error,
						customDefaultMessage: "No se pudo leer la fecha de corte del mayor",
						empty: true,
					});
				})
				.finally(() => {
					this.loadingCutoff = false;
				});
		},
		persistCutoff(value) {
			this.savingCutoff = true;
			return setLedgerCutoff(this.idCompany, value)
				.then(({ data }) => {
					this.cutoffDate = data.ledger_cutoff_date
						? moment(data.ledger_cutoff_date, "YYYY-MM-DD").toDate()
						: null;
					notificate("Fecha de corte del mayor actualizada", "is-success is-light");
				})
				.catch((error) => {
					consoleHandlerError({
						error,
						customDefaultMessage: "No se pudo guardar la fecha de corte del mayor",
						empty: true,
					});
				})
				.finally(() => {
					this.savingCutoff = false;
				});
		},
		saveCutoff() {
			const value = this.cutoffDate
				? moment(this.cutoffDate).format("YYYY-MM-DD")
				: null;
			this.persistCutoff(value);
		},
		clearCutoff() {
			this.persistCutoff(null);
		},
		setLedgerOnly() {
			this.cutoffDate = moment(LEDGER_ONLY_CUTOFF, "YYYY-MM-DD").toDate();
			this.persistCutoff(LEDGER_ONLY_CUTOFF);
		},
		loadLastUpload() {
			getLedgerLastUpload(this.idCompany)
				.then(({ data }) => {
					this.lastUpload = data;
					this.businessTypes = data.business_types || [];
					if (data.business_type) {
						this.businessType = data.business_type;
					}
					// Import en curso al entrar (recarga u otra pestaña): retomar
					// el readout y el sondeo — sin esto la sección deja subir de
					// nuevo y el POST muere en el 409 sin explicación.
					if (data.last_ledger_import_status === "P") {
						this.uploadingLedger = true;
						if (typeof data.last_ledger_import_progress === "number") {
							this.progress = data.last_ledger_import_progress;
						}
						this.pollAttempts = 0;
						this.pollTimer = setTimeout(this.pollImportStatus, POLL_INTERVAL_MS);
					}
				})
				.catch((error) => {
					consoleHandlerError({
						error,
						customDefaultMessage: "No se pudo leer la última subida del mayor",
						empty: true,
					});
				});
		},
		formatDateTime(value) {
			return value ? moment(value).format("DD/MM/YYYY HH:mm") : "";
		},
		onUploaded(file) {
			this.fileLedger = file;
		},
		onRemovedFile() {
			this.fileLedger = null;
		},
		submitLedger() {
			if (!this.fileLedger || this.uploadingLedger) return;
			if (this.uploadMode === "year" && !this.selectedYear) {
				notificate("Elige el año a actualizar.", "is-warning is-light");
				return;
			}
			// "full" = carga inicial (reemplaza todo); un año = subida incremental
			// que reemplaza solo ese año.
			const scope =
				this.uploadMode === "year" ? String(this.selectedYear) : "full";
			this.uploadingLedger = true;
			// 202: el import quedó encolado en RQ; sondeamos el estado. El botón
			// queda "loading" hasta que termine (S/E) o se agote el tope.
			this.progress = null;
			this.invalidRows = [];
			this.importError = "";
			uploadLedger(this.idCompany, this.fileLedger, this.businessType, scope)
				.then(() => {
					this.pollAttempts = 0;
					this.pollImportStatus();
				})
				.catch((error) => {
					this.uploadingLedger = false;
					const data = error.response && error.response.data;
					const httpStatus = error.response && error.response.status;
					if (httpStatus === 409) {
						notificate(
							"Ya hay una importación del mayor en curso para esta empresa.",
							"is-warning is-light"
						);
					} else {
						// El parseo/validación corre async en el worker: el 422 con
						// invalid_rows ya no llega acá, sino por el polling (estado 'E').
						consoleHandlerError({
							error,
							customDefaultMessage: (data && data.error) || "No se pudo importar el mayor",
							empty: true,
						});
					}
				});
		},
		pollImportStatus() {
			getLedgerLastUpload(this.idCompany)
				.then(({ data }) => {
					const importStatus = data.last_ledger_import_status;
					if (importStatus === "S") {
						this.onImportFinished(data, true);
					} else if (importStatus === "E") {
						this.onImportFinished(data, false);
					} else {
						// "P" (o vacío justo tras encolar): actualizar el % (si el
						// worker ya reportó) y reintentar hasta el tope.
						if (typeof data.last_ledger_import_progress === "number") {
							this.progress = data.last_ledger_import_progress;
						}
						this.pollAttempts += 1;
						if (this.pollAttempts >= POLL_MAX_ATTEMPTS) {
							this.uploadingLedger = false;
							notificate(
								"La importación del mayor sigue procesándose. Revisa el estado más tarde.",
								"is-warning is-light"
							);
							return;
						}
						this.pollTimer = setTimeout(this.pollImportStatus, POLL_INTERVAL_MS);
					}
				})
				.catch((error) => {
					this.uploadingLedger = false;
					consoleHandlerError({
						error,
						customDefaultMessage: "No se pudo leer el estado de la importación del mayor",
						empty: true,
					});
				});
		},
		onImportFinished(data, success) {
			this.progress = success ? 100 : this.progress;
			this.uploadingLedger = false;
			this.clearPollTimer();
			// Ya hay respuesta del import (éxito o error): el archivo quedó
			// consumido. Lo limpiamos siempre — para reintentar hay que subir otro.
			// El bump de key resetea el chip interno de UploadFile.
			this.fileLedger = null;
			this.uploadFileKey += 1;
			if (success) {
				notificate(
					`<strong>Mayor importado</strong><br>${data.last_ledger_documents_created} documentos creados de ${data.last_ledger_rows_parsed} filas.`,
					"is-success is-light"
				);
				// Refresca el bloque "Última subida" y los años cargados con esta
				// importación (ledger_years_loaded viene derivado del backend).
				this.lastUpload = {
					last_ledger_upload_at: data.last_ledger_upload_at,
					last_ledger_documents_created: data.last_ledger_documents_created,
					last_ledger_rows_parsed: data.last_ledger_rows_parsed,
					business_super_type: data.business_super_type,
					ledger_years_loaded: data.ledger_years_loaded,
				};
				this.$emit("imported", data);
			} else if (data.invalid_rows && data.invalid_rows.length) {
				// Todo-o-nada: el worker rechazó filas; las mostramos al pie para
				// que el analista corrija el archivo y vuelva a subirlo.
				this.invalidRows = data.invalid_rows;
			} else {
				// Además del toast (que desaparece solo), el error queda fijo en
				// la sección para poder leerlo con calma.
				this.importError = data.last_ledger_import_error || "";
				notificate(
					`<strong>No se pudo importar el mayor</strong><br>${data.last_ledger_import_error || ""}`,
					"is-danger is-light"
				);
			}
		},
		clearPollTimer() {
			if (this.pollTimer) {
				clearTimeout(this.pollTimer);
				this.pollTimer = null;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
// Tema del analista — "mesa de control de cartera" (var(--ap-*),
// elements/_analyst_theme.scss). Superficies: paper (base) → mist (paneles
// insertos). Acento teal reservado para estado/foco. Cifras en tabular-nums.
// Sección de la ficha (renderiza dentro de .ci-panel de CompanyInfo): mismo
// encabezado eyebrow+heading que las otras facetas.

// -- Shell ----------------------------------------------------------------
// Grid de dos columnas: header a lo ancho; flujo (body fila 2 + footer fila 3)
// a la izquierda, contexto a la derecha. Sin wrappers extra: cada hijo se
// ubica por área. Bajo 900px colapsa a una columna (flujo primero).
.ci-upload-ledger {
	font-family: var(--ap-font);
	color: var(--ap-ink-2);
	display: grid;
	grid-template-columns: minmax(0, 1fr) 300px;
	column-gap: 24px;
}

.ul-head {
	grid-column: 1 / -1;
}

.ledger-body {
	grid-column: 1;
	grid-row: 2;
}

.ledger-foot {
	grid-column: 1;
	grid-row: 3;
}

.ul-side {
	grid-column: 2;
	grid-row: 2 / span 2;
	align-self: start;
	position: sticky;
	top: 24px;
}

@media (max-width: 900px) {
	.ci-upload-ledger {
		grid-template-columns: minmax(0, 1fr);
	}

	.ledger-body,
	.ledger-foot,
	.ul-side {
		grid-column: 1;
		grid-row: auto;
	}

	.ul-side {
		position: static;
		margin-top: 16px;
	}
}

// -- Aviso de último intento fallido (columna de contexto) ------------------
.ul-last-error {
	margin-bottom: 12px;
	padding: 12px 14px;
	background-color: var(--ap-alert-wash);
	border: 1px solid rgba(192, 69, 59, 0.28);
	border-radius: var(--ap-radius-sm);
}

.ul-last-error-title {
	display: block;
	font-size: 12.5px;
	font-weight: 700;
	color: var(--ap-alert);
}

.ul-last-error-text {
	margin: 4px 0 0;
	font-size: 12px;
	line-height: 1.45;
	color: var(--ap-ink-2);
}

// -- Estado vacío de la columna de contexto ----------------------------------
.ul-empty {
	padding: 14px;
	background-color: var(--ap-mist);
	border: 1px dashed var(--ap-line-strong);
	border-radius: var(--ap-radius-sm);
}

.ul-empty-title {
	display: block;
	font-size: 12.5px;
	font-weight: 700;
	color: var(--ap-ink);
}

.ul-empty-text {
	margin: 4px 0 0;
	font-size: 12px;
	line-height: 1.45;
	color: var(--ap-slate);
}

// -- Header ---------------------------------------------------------------
.ul-head {
	margin-bottom: 16px;
}

.ul-eyebrow {
	margin: 0 0 6px;
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	color: var(--ap-muted);
}

.ul-heading {
	margin: 0;
	font-size: 17px;
	font-weight: 700;
	letter-spacing: -0.015em;
	color: var(--ap-ink);
}

.ul-sub {
	margin: 4px 0 0;
	font-size: 12.5px;
	line-height: 1.4;
	color: var(--ap-slate);
}

// -- Body -----------------------------------------------------------------
.ledger-body {
	background-color: var(--ap-paper);
}

.field-group {
	margin-bottom: 12px;
}

.field-group:last-child {
	margin-bottom: 0;
}

// Eyebrow: label instrumental de cada bloque + nota (badge) opcional.
.field-eyebrow {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 8px;
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: var(--ap-muted);
}

// Firma del panel: tick teal de "instrumento" antes de cada sección — extiende
// el borde-teal de la franja de estado y unifica la sección como una superficie
// de control. Es el único gesto de acento recurrente; el resto queda quieto.
.field-eyebrow::before {
	content: "";
	flex: none;
	width: 2px;
	height: 10px;
	border-radius: 1px;
	background-color: var(--ap-teal);
}

.field-eyebrow-note {
	padding: 1px 8px;
	font-size: 10px;
	font-weight: 600;
	letter-spacing: 0.03em;
	text-transform: none;
	color: var(--ap-slate);
	background-color: var(--ap-mist);
	border: 1px solid var(--ap-line);
	border-radius: var(--ap-radius-pill);
}

.field-hint {
	margin-bottom: 10px;
	font-size: 12.5px;
	line-height: 1.45;
	color: var(--ap-slate);

	strong {
		color: var(--ap-ink-2);
		font-weight: 600;
	}
}

// -- Alcance: toggle + selector de año en una sola fila -------------------
.scope-row {
	display: flex;
	align-items: center;
	gap: 10px;
}

// Sin el margin-bottom por defecto de b-field: la fila los alinea.
.scope-row :deep(.field){
	margin-bottom: 0;
}

// Toggle y select comparten EXACTAMENTE la misma altura: Bulma le da al
// `<select>` una altura propia (2.5em) que no coincidía con los botones del
// toggle y se veía desalineado. Fijamos 36px en ambos.
.scope-row :deep(.b-radio.button),
.scope-row :deep(.select),
.scope-row :deep(.select select){
	height: 36px;
}

.scope-row :deep(.select select){
	padding-top: 0;
	padding-bottom: 0;
	font-size: 13px;
	border-radius: var(--ap-radius-sm);
}

// -- Contexto: última importación (strip mist con acento teal) ------------
.last-upload {
	display: flex;
	align-items: center;
	gap: 16px;
	margin-bottom: 12px;
	padding: 8px 12px;
	background-color: var(--ap-mist);
	border: 1px solid var(--ap-line);
	border-left: 3px solid var(--ap-teal);
	border-radius: var(--ap-radius-sm);
}

.last-upload-main {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.last-upload-eyebrow {
	font-size: 10.5px;
	font-weight: 600;
	letter-spacing: 0.07em;
	text-transform: uppercase;
	color: var(--ap-muted);
}

.last-upload-date {
	font-size: 13px;
	font-weight: 600;
	color: var(--ap-ink);
}

.last-upload-by {
	font-size: 11.5px;
	color: var(--ap-muted);
	overflow-wrap: anywhere;
}

.last-upload-stats {
	display: flex;
	gap: 18px;
}

.stat {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	line-height: 1.1;
}

// Divisoria fina entre cifras: lectura de instrumento (docs | filas).
.stat + .stat {
	padding-left: 18px;
	border-left: 1px solid var(--ap-line);
}

.stat-value {
	font-size: 17px;
	font-weight: 700;
	color: var(--ap-teal-deep);
	font-variant-numeric: tabular-nums;
	letter-spacing: -0.01em;
}

.stat-label {
	font-size: 10px;
	font-weight: 600;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	color: var(--ap-muted);
}

.ledger-only-hint {
	margin: 8px 0 0;
	font-size: 12px;
	font-weight: 500;
	color: var(--ap-teal-deep);
}

.cutoff-actions {
	display: flex;
	justify-content: flex-end;
	gap: 8px;
	margin-top: 12px;
}

// Jerarquía: el único primario de la sección es el CTA del footer ("Subir mayor").
// "Guardar corte" es la acción de esta sub-sección → secundario teal (no un
// segundo botón oscuro que compita con el CTA principal).
.cutoff-actions :deep(.button.is-primary){
	background-color: var(--ap-teal-wash);
	border-color: var(--ap-teal);
	color: var(--ap-teal-deep);
}

.cutoff-actions :deep(.button.is-primary:hover:not([disabled])){
	background-color: var(--ap-teal);
	border-color: var(--ap-teal);
	color: var(--ap-paper);
	transform: none;
}

// -- Tipo de negocio: 5 radios (uno solo), una columna por super_type -----
.business-types {
	display: flex;
	gap: 24px;
}

// Cada columna se reparte el ancho disponible (mitad y mitad) para no dejar
// espacio vacío a la derecha.
.business-type-col {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.business-type-col-head {
	margin-bottom: 2px;
	font-size: 10.5px;
	font-weight: 600;
	letter-spacing: 0.06em;
	text-transform: uppercase;
	color: var(--ap-muted);
}

.business-types :deep(.b-radio.radio){
	display: flex;
	align-items: center;
	margin-right: 0;
	font-size: 13px;
	font-weight: 600;
	color: var(--ap-ink-2);
}

// El punto del radio en teal cuando está seleccionado.
.business-types :deep(.b-radio.radio input[type="radio"]:checked + .check){
	border-color: var(--ap-teal);
	background-color: var(--ap-teal);
}

.business-types :deep(.b-radio.radio:hover .check){
	border-color: var(--ap-teal);
}

// -- Dropzone (UploadFile compartido) -------------------------------------
// El b-upload drag&drop es inline-flex y encoge al ancho del texto; lo
// forzamos a bloque de ancho completo y lo vestimos como dropzone punteado.
.ledger-body :deep(.uploadFile),
.ledger-body :deep(.upload),
.ledger-body :deep(.upload .upload-draggable){
	display: block;
	width: 100%;
	max-width: 100%;
	box-sizing: border-box;
	// El estilo global `_upload_file.scss` fija `.uploadFile`/`.section` en
	// height:162px (deja el dropzone alto con el contenido arriba). Acá lo
	// liberamos para que el box abrace su contenido.
	height: auto;
}

.ledger-body :deep(.upload .upload-draggable){
	padding: 0;
	border: 1.5px dashed var(--ap-line-strong);
	border-radius: var(--ap-radius-sm);
	background-color: var(--ap-mist);
	transition: border-color 0.14s var(--ap-ease), background-color 0.14s var(--ap-ease);
}

.ledger-body :deep(.upload .upload-draggable:hover),
.ledger-body :deep(.upload .upload-draggable.is-hovered){
	border-color: var(--ap-teal);
	background-color: var(--ap-teal-wash);
}

// Bulma le mete `padding: 3rem 1.5rem` a `.section`; acá lo apretamos para que el
// dropzone abrace el contenido (ícono + 2 líneas) sin aire vertical de sobra.
.ledger-body :deep(.upload .section){
	width: 100%;
	height: auto;
	padding: 8px 16px;
	box-sizing: border-box;
}

.ledger-body :deep(.upload .content),
.ledger-body :deep(.upload .content p){
	max-width: 100%;
	white-space: normal;
	overflow-wrap: anywhere;
}

.ledger-body :deep(.upload .content p:not(.has-text-grey-light)){
	font-size: 13px;
	font-weight: 500;
	color: var(--ap-ink-2);
}

.ledger-body :deep(.upload .content .has-text-grey-light){
	font-size: 12px;
	color: var(--ap-muted) !important;
}

.ledger-body :deep(.upload .icon),
.ledger-body :deep(.upload .icon.has-text-primary){
	color: var(--ap-teal) !important;
}

// Icono e interlineado compactos: el default `is-large` + los márgenes de
// `.content` hacían el dropzone innecesariamente alto.
.ledger-body :deep(.upload .icon.is-large){
	height: 1.6rem;
	width: 1.6rem;
	margin: 0 auto;
}

.ledger-body :deep(.upload .icon.is-large .mdi::before){
	font-size: 1.4rem;
}

// Interlineado compacto: el `<p>` del ícono y el líder del texto dejaban aire.
.ledger-body :deep(.upload .content){
	line-height: 1.3;
}

.ledger-body :deep(.upload .content p:not(:last-child)){
	margin-bottom: 2px;
}

// Chip del archivo seleccionado: fila limpia sobre mist, no un pill teal.
// margin-bottom explícito: Bulma le mete un margin negativo a `.tags` que, sin
// esto, solapaba el panel de errores de abajo.
.ledger-body :deep(.uploadFile .tags){
	margin-top: 8px;
	margin-bottom: 0;
}

.ledger-body :deep(.uploadFile .tag.is-primary){
	display: inline-flex;
	align-items: center;
	max-width: 100%;
	height: auto;
	margin-bottom: 0;
	padding: 6px 8px 6px 10px;
	background-color: var(--ap-mist);
	border: 1px solid var(--ap-line);
	border-left: 3px solid var(--ap-teal);
	border-radius: var(--ap-radius-sm);
	color: var(--ap-ink-2);
	font-size: 12.5px;
	font-weight: 500;
	white-space: normal;
	overflow-wrap: anywhere;
}

.ledger-body :deep(.uploadFile .tag.is-primary .delete){
	flex: none;
	margin-left: 10px;
	background-color: var(--ap-slate);
}

.ledger-body :deep(.uploadFile .tag.is-primary .delete:hover){
	background-color: var(--ap-alert);
}

// -- Readout del import (signature) ---------------------------------------
.import-readout {
	padding: 16px;
	background-color: var(--ap-mist);
	border: 1px solid var(--ap-line);
	border-radius: var(--ap-radius-sm);
}

.import-readout-head {
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 12px;
}

.import-readout-text {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.import-readout-title {
	font-size: 13px;
	font-weight: 600;
	color: var(--ap-ink);
}

.import-readout-file {
	font-size: 12px;
	color: var(--ap-slate);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.import-readout-pct {
	flex: none;
	font-size: 18px;
	font-weight: 700;
	color: var(--ap-teal-deep);
	font-variant-numeric: tabular-nums;
	letter-spacing: -0.01em;
}

.import-readout-pct-unit {
	margin-left: 1px;
	font-size: 11px;
	font-weight: 600;
	color: var(--ap-slate);
}

// Pista de progreso: teal determinado, o segmento deslizante indeterminado.
.track {
	position: relative;
	height: 8px;
	border-radius: var(--ap-radius-pill);
	background-color: var(--ap-line);
	overflow: hidden;
}

.track-fill {
	height: 100%;
	border-radius: inherit;
	background: linear-gradient(90deg, var(--ap-teal), var(--ap-teal-deep));
	transition: width 0.4s var(--ap-ease);
}

.track.is-indeterminate .track-fill {
	display: none;
}

.track.is-indeterminate::after {
	content: "";
	position: absolute;
	top: 0;
	bottom: 0;
	width: 38%;
	border-radius: inherit;
	background: linear-gradient(
		90deg,
		transparent,
		var(--ap-teal),
		transparent
	);
	animation: track-slide 1.4s var(--ap-ease) infinite;
}

@keyframes track-slide {
	0% {
		transform: translateX(-120%);
	}
	100% {
		transform: translateX(320%);
	}
}

// -- Filas inválidas (todo-o-nada): panel de alerta con lista scrolleable ---
.import-errors {
	margin-top: 12px;
	padding: 12px 14px;
	background-color: var(--ap-alert-wash);
	border: 1px solid var(--ap-alert);
	border-radius: var(--ap-radius-sm);
}

.import-errors-head {
	font-size: 13px;
	font-weight: 700;
	color: var(--ap-alert);
}

.import-errors-hint {
	margin-top: 2px;
	margin-bottom: 10px;
	font-size: 12px;
	color: var(--ap-slate);
}

.import-errors-list {
	max-height: 180px;
	overflow-y: auto;
	margin: 0;
	padding: 0;
	list-style: none;
	border-top: 1px solid var(--ap-line);
}

.import-errors-item {
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	gap: 4px 10px;
	padding: 8px 0;
	border-bottom: 1px solid var(--ap-line);
	font-size: 12.5px;
}

.import-errors-row {
	flex: none;
	font-weight: 700;
	color: var(--ap-ink);
	font-variant-numeric: tabular-nums;
}

.import-errors-reason {
	flex: 1;
	min-width: 160px;
	color: var(--ap-ink-2);
}

.import-errors-meta {
	flex: none;
	font-size: 11.5px;
	color: var(--ap-muted);
	overflow-wrap: anywhere;
}

// -- Controles (datepicker + select): mismo borde/tema + ring de foco teal --
// El `.input` del datepicker y el `<select>` del año comparten tratamiento para
// que se lean como controles hermanos, no como widgets de librerías distintas.
.ledger-body :deep(.input),
.ledger-body :deep(.select select){
	border: 1px solid var(--ap-line-strong);
	border-radius: var(--ap-radius-sm);
	font-family: var(--ap-font);
	font-size: 13.5px;
	color: var(--ap-ink);
	box-shadow: none;
	transition: border-color 0.14s var(--ap-ease), box-shadow 0.14s var(--ap-ease);
}

.ledger-body :deep(.input:focus),
.ledger-body :deep(.input:focus-visible),
.ledger-body :deep(.select select:focus){
	border-color: var(--ap-teal);
	box-shadow: 0 0 0 3px var(--ap-teal-wash);
	outline: none;
}

// Caret del select en teal, coherente con el acento del panel.
.ledger-body :deep(.select:not(.is-multiple):not(.is-loading)::after){
	border-color: var(--ap-teal);
}

// -- Footer ---------------------------------------------------------------
.ledger-foot {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 8px;
	margin-top: 16px;
	padding-top: 12px;
	border-top: 1px solid var(--ap-line);
}

// Pista de por qué "Subir mayor" está deshabilitado; el margin auto la empuja a
// la izquierda y deja los botones a la derecha.
.foot-hint {
	margin-right: auto;
	font-size: 12px;
	color: var(--ap-slate);
}

// -- Botones (Buefy) alineados al tema ------------------------------------
.ci-upload-ledger :deep(.button){
	font-family: var(--ap-font);
	font-weight: 600;
	font-size: 13px;
	height: auto;
	padding: 7px 14px;
	border-radius: var(--ap-radius-sm);
	transition: transform 0.14s var(--ap-ease), background-color 0.14s var(--ap-ease),
		border-color 0.14s var(--ap-ease), color 0.14s var(--ap-ease);

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 2px;
	}

	&[disabled] {
		opacity: 0.55;
		cursor: not-allowed;
	}
}

// Footer CTA un poco más generoso en horizontal que los botones inline.
.ledger-foot .button {
	padding: 8px 18px;
}

.ci-upload-ledger :deep(.button.is-primary){
	background-color: var(--ap-ink);
	border-color: var(--ap-ink);
	color: var(--ap-paper);

	&:hover:not([disabled]) {
		background-color: var(--ap-teal-deep);
		border-color: var(--ap-teal-deep);
		transform: translateY(-1px);
	}

	&:active:not([disabled]) {
		transform: translateY(0);
	}
}

.ci-upload-ledger :deep(.button.is-light){
	background-color: var(--ap-mist);
	border: 1px solid var(--ap-line);
	color: var(--ap-slate);

	&:hover:not([disabled]) {
		border-color: var(--ap-line-strong);
		color: var(--ap-ink-2);
	}
}

@media (prefers-reduced-motion: reduce) {
	.track.is-indeterminate::after {
		animation: none;
	}

	.track-fill {
		transition: none;
	}
}
</style>
